import { type IClient, normalizeName } from './clients.js';
import type { IExtraction } from './decide.js';
import { ACCOUNTING_CA, docTypeOf, type IPack } from '../packs/index.js';

/**
 * A fix the owner asked AI Admin to remember. Rules are plain and visible (the "What I've
 * learned" list), never model training, so they can be reviewed, removed and measured.
 *
 * - alias: a way a client's name is written, e.g. "Maple Ridge Dental Group Inc." is MapleRidgeDental.
 * - issuer: documents from a sender are always this type, e.g. Rogers Business sends Bills.
 *   Optionally only for one client.
 */
export interface ILearnedRule {
  id: string;
  kind: 'alias' | 'issuer';
  /** alias: the name as written in documents. */
  written?: string;
  /** alias: the client it means; issuer: limits the rule to this client (optional). Short name. */
  client?: string;
  /** issuer: the sender as written. */
  issuer?: string;
  /** issuer: the DOC_TYPES id to use. */
  docType?: string;
  /** Where it came from, for people: e.g. "From your fix to Rogers_May.pdf". */
  source?: string;
  createdAt: string;
  createdBy?: string;
  used?: number;
}

/** Confidence a learned rule gives the field it decides. */
export const LEARNED_CONFIDENCE: number = 0.95;

/** The client list with every learned alias added, so matching treats them as exact. */
export function clientsWithAliases(clients: IClient[], rules: ILearnedRule[]): IClient[] {
  return clients.map(c => {
    const extra: string[] = rules
      .filter(r => r.kind === 'alias' && r.client === c.short && !!r.written)
      .map(r => r.written!);
    return extra.length ? { ...c, aliases: [...(c.aliases ?? []), ...extra] } : c;
  });
}

/**
 * Applies learned issuer rules to what the AI read, before the decision. Returns the new
 * extraction and the ids of the rules that were used. Alias rules act through clientsWithAliases.
 */
export function applyLearned(x: IExtraction, rules: ILearnedRule[], clients: IClient[], pack: IPack = ACCOUNTING_CA): { extraction: IExtraction; used: string[] } {
  const used: string[] = [];
  let out: IExtraction = x;
  const issuer: string = normalizeName(x.issuer ?? '');
  if (issuer) {
    for (const r of rules) {
      if (r.kind !== 'issuer' || !r.issuer || !docTypeOf(pack, r.docType) || normalizeName(r.issuer) !== issuer) {
        continue;
      }
      if (r.client) {
        const client: IClient | undefined = clients.filter(c => c.short === r.client)[0];
        const written: string = normalizeName(x.client ?? '');
        const names: string[] = client ? [client.name, ...(client.aliases ?? [])].map(normalizeName) : [];
        if (!written || names.indexOf(written) === -1) {
          continue;
        }
      }
      out = { ...out, docType: r.docType, docTypeConfidence: Math.max(out.docTypeConfidence ?? 0, LEARNED_CONFIDENCE) };
      used.push(r.id);
      break;
    }
  }
  for (const r of rules) {
    if (r.kind === 'alias' && r.written && x.client && normalizeName(r.written) === normalizeName(x.client)) {
      used.push(r.id);
    }
  }
  return { extraction: out, used };
}

/** A sentence for the "What I've learned" list. */
export function describeRule(r: ILearnedRule, clients: IClient[], pack: IPack = ACCOUNTING_CA): string {
  const name = (short: string | undefined): string => clients.filter(c => c.short === short)[0]?.name ?? short ?? '';
  if (r.kind === 'alias') {
    return `"${r.written}" is ${name(r.client)}`;
  }
  const type: string = docTypeOf(pack, r.docType)?.label ?? r.docType ?? '';
  return r.client ? `${r.issuer} documents for ${name(r.client)} are: ${type}` : `${r.issuer} documents are: ${type}`;
}
